import { type BoxStaffApiGateway } from "@modules/boxStaff/gateway/boxStaffApiGateway"
import { LoggerService } from "@modules/common/logger/loggerService"
import {
  BoxStaffActionRequest,
  type BoxStaffAddInput,
  type BoxStaffDetailedDto,
  type BoxStaffDetailedUpadteInput,
  type BoxStaffInvitationDetailsDto,
  BoxStaffInvitationDetailsDtoSchema,
  type BoxStaffItemDto,
  BoxStaffItemDtoSchema,
  BoxStaffStatus,
  StaffColumnToSort,
} from "@modules/boxStaff/gateway/boxStaffDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { type Page, type Paginated, toPage } from "@modules/utils/paginated"

export class BoxStaffApi implements BoxStaffApiGateway {
  private baseUrl = "/box/staff"

  constructor(private api: ApiClient) {}

  async getBoxStaffs(
    page = 0,
    size = 10,
    nameFilter?: string,
    fetchArchived?: boolean,
    litsOfStatus?: BoxStaffStatus[],
    sortBy?: StaffColumnToSort,
    sortOrder?: ColumnSortingOrder,
  ): Promise<Page<BoxStaffItemDto>> {
    const result = await this.api.get<Paginated<BoxStaffItemDto>>(`${this.baseUrl}`, {
      params: {
        page,
        size,
        usernameOrNameSearch: nameFilter,
        columnToSort: sortBy,
        columnSortingOrder: sortOrder,
        fetchArchived,
        litsOfStatus,
      },
      paramsSerializer: {
        indexes: null,
      },
    })
    const pageResult = toPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((staff) => LoggerService.parseApiDto(BoxStaffItemDtoSchema, staff)),
    }
  }

  async getBoxStaff(staffId: string): Promise<BoxStaffDetailedDto> {
    const resultDto = await this.api.get<BoxStaffDetailedDto>(`${this.baseUrl}/${staffId}`)
    return resultDto.data
  }

  async addBoxStaff(input: BoxStaffAddInput): Promise<BoxStaffItemDto> {
    const result = await this.api.post(`${this.baseUrl}`, { ...input })
    return LoggerService.parseApiDto(BoxStaffItemDtoSchema, result.data)
  }

  async responseBoxStaffInvitation(boxId: string, action: BoxStaffActionRequest): Promise<void> {
    return await this.api.post(`${this.baseUrl}/${action.toLocaleLowerCase()}`, { boxId })
  }

  async updateBoxStaff(staffId: string, input: BoxStaffDetailedUpadteInput): Promise<void> {
    await this.api.put(`${this.baseUrl}/${staffId}`, input)
  }

  async deleteBoxStaff(staffId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${staffId}`)
  }

  async getBoxStaffInvitationDetails(boxId: string): Promise<BoxStaffInvitationDetailsDto> {
    const result = await this.api.get(`${this.baseUrl}/invitation/details`, {
      headers: {
        "x-box-id": boxId,
      },
    })
    return LoggerService.parseApiDto(BoxStaffInvitationDetailsDtoSchema, result.data)
  }
}
